/**
 * PricingCard Molecule Component
 * Price display + features list for pricing tiers
 */

import React from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Button } from "@/components/atoms/Button";
import type { PricingCardBlok } from "@/types";

export interface PricingCardProps {
  blok: PricingCardBlok;
}

export const PricingCard: React.FC<PricingCardProps> = ({ blok }) => {
  const {
    plan_name,
    price,
    currency = "€",
    period = "mo",
    features,
    cta_text,
    cta_link,
    is_popular = false,
  } = blok;
  // Convert features string (multiline text) to array
  const featureList = features
    ? features.split("\n").filter((f) => f.trim())
    : [];

  // Pro plan (is_popular) has dark background with orange accent, Starter has orange background
  const bgColor = is_popular ? "bg-gray-900" : "bg-primary";
  const priceColor = is_popular ? "text-primary" : "text-black";
  const borderColor = is_popular ? "border-primary" : "border-black";
  const bulletColor = is_popular ? "text-primary" : "text-black";
  const featureTextColor = is_popular ? "text-white" : "text-black";
  const buttonVariant = is_popular ? "primary" : "black";

  // The display size is tuned for two-digit prices; a four-digit one (1250)
  // overflows the card at that scale, so step it down as the number grows.
  const digits = String(price ?? "").replace(/\D/g, "").length;
  const priceSize =
    digits >= 4
      ? "text-3xl sm:text-4xl md:text-6xl"
      : digits === 3
        ? "text-3xl sm:text-5xl md:text-7xl"
        : "text-3xl sm:text-5xl md:text-8xl";
  const suffixSize =
    digits >= 4
      ? "text-lg sm:text-2xl md:text-4xl"
      : "text-xl sm:text-3xl md:text-6xl";

  return (
    <div
      {...storyblokEditable(blok)}
      className={`relative rounded-2xl md:rounded-3xl flex flex-col h-full p-5 md:p-8 border-4 md:border-[6px] ${borderColor} ${bgColor}`}
    >
      {/* Plan Name */}
      <h3 className={`text-xl md:text-2xl font-archivo-black leading-snug ${priceColor} text-center mb-4 md:mb-8`}>
        {plan_name}
      </h3>

      {/* Price */}
      <div className="flex flex-wrap items-baseline justify-center gap-x-1 md:gap-x-2 mb-6 md:mb-12">
        <span
          className={`${priceSize} font-archivo-black ${priceColor} leading-none tracking-tight`}
        >
          {price}
        </span>
        <span
          className={`${suffixSize} font-black ${priceColor}`}
        >
          {currency}
        </span>
        <span
          className={`${suffixSize} font-black ${priceColor}`}
        >
          /{period}
        </span>
      </div>

      {/* Features List */}
      <ul className="flex flex-col gap-3 md:gap-5 mb-6 md:mb-12 flex-grow">
        {featureList.map((feature, index) => {
          // Split by colon to separate label from description
          const colonIndex = feature.indexOf(":");
          const hasLabel = colonIndex > 0;
          const label = hasLabel ? feature.substring(0, colonIndex) : "";
          const description = hasLabel
            ? feature.substring(colonIndex + 1).trim()
            : feature;

          return (
            <li key={index} className="flex items-start gap-4">
              <span
                className={`flex-shrink-0 w-3 h-3 mt-1.5 ${bulletColor.replace(
                  "text-",
                  "bg-"
                )}`}
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }}
              />
              <span
                className={`text-sm md:text-base ${featureTextColor} leading-relaxed`}
              >
                {hasLabel ? (
                  <>
                    <strong className="font-bold">{label}:</strong>{" "}
                    {description}
                  </>
                ) : (
                  feature
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {/* CTA Button */}
      {cta_text && (
        <div className="mt-auto flex justify-center">
          <Button
            href={cta_link?.cached_url || cta_link?.url}
            variant={buttonVariant}
            size="md"
            className="border-0 font-bold"
          >
            {cta_text}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PricingCard;
